import { combineReducers } from 'redux';
import { createMigrate, createTransform, MigrationManifest, PersistConfig, persistReducer } from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/es/stateReconciler/autoMergeLevel2';
import { bleReducer } from 'src/redux/ble/reducer';
import { annotationReducer } from './annotation/reducer';
import appBar from './appBar/reducer';
import { authReducer } from './auth/reducer';
import { commonReducer } from './common/reducer';
import { logReducer } from './log/reducer';
import { capacitorStorage } from './persist';
import { preferencesReducer } from './profile-preferences/reducer';
import { sentinelReducer } from './sentinel/reducer';
import { organizationApi } from './api/organizations';
import { authActions } from './auth/actions';
import { userApi } from './api/users';
import { bundlesApi } from './api/bundles';
import { firmwareTiersApi } from './api/firmwareTiers';
import { logInfoReducer } from './logInfo/reducer';
import { logTagsReducer } from './logTags/reducer';

const rtkQueryReducers = {
  [organizationApi.reducerPath]: organizationApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [bundlesApi.reducerPath]: bundlesApi.reducer,
  [firmwareTiersApi.reducerPath]: firmwareTiersApi.reducer,
} as const;

const _rootReducer = combineReducers({
  annotation: annotationReducer,
  auth: authReducer,
  appBar,
  preferencesReducer,
  ble: bleReducer,
  log: logReducer,
  common: commonReducer,
  sentinel: sentinelReducer,
  tryFetchLogInfo: logInfoReducer,
  tryFetchLogTags: logTagsReducer,
  ...rtkQueryReducers,
});

/** Adds a global listener for log out that clears all state back to initial */
const withLogOutRootReducer: typeof _rootReducer = (state, action) => {
  if (authActions.logOut.match(action)) {
    return _rootReducer(undefined, action);
  }

  return _rootReducer(state, action);
};

const resetAllMigrator: MigrationManifest[number] = (state: any) =>
  state ? { auth: state.auth, _persist: state._persist } : undefined;

const version2Migrator: MigrationManifest[number] = (state: any) => {
  if (!state) return undefined;
  try {
    delete state[organizationApi.reducerPath]?.config;
    delete state[firmwareTiersApi.reducerPath]?.config;
    delete state[organizationApi.reducerPath]?.config;
    delete state[userApi.reducerPath]?.config;
  } catch {}

  return state;
};

const version3Migrator: MigrationManifest[number] = (state: any) => {
  if (!state) return undefined;
  try {
    delete state.sentinel?.bundles;
  } catch {}
  return state;
};

const logDataMigrator: MigrationManifest[number] = (state: any) => {
  if (!state) return undefined;
  try {
    delete state.log;
    delete state.sentinel;
  } catch {}
  return state;
};

const persistMigrations: MigrationManifest = {
  0: resetAllMigrator,
  1: resetAllMigrator,
  2: version2Migrator,
  3: version3Migrator,
  4: logDataMigrator,
  5: logDataMigrator,
  6: logDataMigrator,
};

const rtkQueryTransform = createTransform(
  inboundState => {
    const next = { ...(inboundState as any) };
    delete (next as any)?.config;
    return next;
  },
  outboundState => {
    const next = { ...(outboundState as any) };
    delete (next as any)?.config;
    return next;
  },
  { whitelist: Object.keys(rtkQueryReducers) },
);

export const rootPersistConfig: PersistConfig<ReturnType<typeof withLogOutRootReducer>> = {
  key: 'root',
  storage: capacitorStorage,
  blacklist: ['ble', 'common', 'appBar', 'log', 'sentinel'],
  transforms: [rtkQueryTransform],
  debug: false,
  version: 6,
  migrate: createMigrate(persistMigrations),
  stateReconciler: autoMergeLevel2,
  throttle: 3000,
  writeFailHandler: err => {
    console.error('Failed to persist state', err);
  },
};

const rootReducer = persistReducer(rootPersistConfig, withLogOutRootReducer);
export default rootReducer;
